import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import Swal from 'sweetalert2'

import { CashBoxBalancePolicy } from './CashBoxBalancePolicy'
import { KTIcon } from '../../../../../_metronic/helpers'
import {
  getCashBoxDetails,
  updateCashBoxStatus,
  attachCashBoxManager,
  detachCashBoxManager,
  getMerchantManagersForSelect,
  blockCashBox,
  unblockCashBox,
  setCashBoxApiAccess,
  rotateCashBoxApiToken,
} from '../../../../common/core/_requests'
import { ICashBoxDetailed, ICashier } from '../../../../../types'
import { NewEmployee } from '../../../staff/components/NewEmployee'
import { ROLES_FOR_CASHBOX } from '../../../staff/components/NewEmployee/consts'
import LedgerWalletBalanceCard from '../../components/LedgerWalletBalance'
import { ROLE, useAuth } from '../../../auth'
import { IsGranted } from '../../../auth/PrivateRoute'

const CashBoxDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const canManage = IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER], currentUser)
  // CPAYMENT-1546: block/overdraft controls also available to merchant-managers (scoped server-side)
  const canManageBlock = IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_MANAGER], currentUser)
  const [cashbox, setCashbox] = useState<ICashBoxDetailed | null>(null)
  const [loading, setLoading] = useState(false)
  const [statusLoading, setStatusLoading] = useState(false)
  const [availableManagers, setAvailableManagers] = useState<Array<{ id: number; uuid: string }>>([])
  const [selectedManagerId, setSelectedManagerId] = useState<string>('')
  const [managerLoading, setManagerLoading] = useState(false)
  // CPAYMENT-1693 (временное решение): программный доступ кассы
  const [apiAccessLoading, setApiAccessLoading] = useState(false)

  const fetchCashBoxDetails = async () => {
    if (!id) return

    setLoading(true)
    try {
      const response = await getCashBoxDetails(Number(id))
      
      if (response.data?.success && response.data.cashbox) {
        setCashbox(response.data.cashbox)
      }
    } catch (error) {
      console.error('Ошибка загрузки данных кассы:', error)
      Swal.fire('Ошибка', 'Не удалось загрузить данные кассы', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCashBoxDetails()
  }, [id])

  const handleStatusChange = async (enabled: boolean) => {
    if (!cashbox) return

    // Показываем подтверждение
    const result = await Swal.fire({
      title: 'Подтверждение действия',
      text: `Вы действительно хотите ${enabled ? 'активировать' : 'деактивировать'} кассу "${cashbox.title}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Да, изменить',
      cancelButtonText: 'Отмена',
      confirmButtonColor: '#009ef7',
      cancelButtonColor: '#f1416c',
    })

    // Если пользователь отменил действие
    if (!result.isConfirmed) {
      return
    }

    setStatusLoading(true)
    
    try {
      const response = await updateCashBoxStatus(cashbox.id, { enabled })
      
      if (response.data?.success && response.data.data) {
        setCashbox(response.data.data)
        Swal.fire(
          'Статус обновлен',
          `Касса ${enabled ? 'активирована' : 'деактивирована'}`,
          'success'
        )
      } else {
        Swal.fire('Ошибка', 'Неожиданный ответ от сервера', 'error')
      }
    } catch (error) {
      console.error('Ошибка обновления статуса:', error)
      Swal.fire('Ошибка', 'Не удалось обновить статус кассы', 'error')
    } finally {
      setStatusLoading(false)
    }
  }

  // CPAYMENT-1546: block / unblock cassa
  const handleBlockToggle = async () => {
    if (!cashbox) return

    if (cashbox.blocked) {
      const result = await Swal.fire({
        title: 'Разблокировать кассу?',
        text: `Касса "${cashbox.title}" снова начнёт участвовать в выплатах.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Разблокировать',
        cancelButtonText: 'Отмена',
      })
      if (!result.isConfirmed) return
      try {
        const response = await unblockCashBox(cashbox.id)
        if (response.data?.success) {
          await fetchCashBoxDetails()
          Swal.fire('Готово', 'Касса разблокирована', 'success')
        } else {
          Swal.fire('Ошибка', 'Неожиданный ответ от сервера', 'error')
        }
      } catch (error) {
        console.error('Ошибка разблокировки:', error)
        Swal.fire('Ошибка', 'Не удалось разблокировать кассу', 'error')
      }
      return
    }

    const { value: reason, isConfirmed } = await Swal.fire({
      title: 'Заблокировать кассу?',
      input: 'text',
      inputLabel: 'Причина блокировки (необязательно)',
      inputPlaceholder: 'напр. превышен овердрафт',
      showCancelButton: true,
      confirmButtonText: 'Заблокировать',
      cancelButtonText: 'Отмена',
    })
    if (!isConfirmed) return
    try {
      const response = await blockCashBox(cashbox.id, reason || '')
      if (response.data?.success) {
        await fetchCashBoxDetails()
        Swal.fire('Готово', 'Касса заблокирована', 'success')
      } else {
        Swal.fire('Ошибка', 'Неожиданный ответ от сервера', 'error')
      }
    } catch (error) {
      console.error('Ошибка блокировки:', error)
      Swal.fire('Ошибка', 'Не удалось заблокировать кассу', 'error')
    }
  }

  const handleEmployeeAdded = () => {
    // Обновляем данные кассы после добавления сотрудника
    fetchCashBoxDetails()
  }

  // AC4: список менеджеров мерчантов для привязки к кассе (только ADMIN/MANAGER)
  useEffect(() => {
    if (!canManage) return
    getMerchantManagersForSelect()
      .then((response) => {
        if (response.data?.success) {
          setAvailableManagers(response.data.employees || [])
        }
      })
      .catch((error) => console.error('Ошибка загрузки менеджеров мерчантов:', error))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage])

  const handleAttachManager = async () => {
    if (!cashbox || !selectedManagerId) return
    setManagerLoading(true)
    try {
      const response = await attachCashBoxManager(cashbox.id, Number(selectedManagerId))
      if (response.data?.success && response.data.cashbox) {
        setCashbox(response.data.cashbox)
        setSelectedManagerId('')
      }
    } catch (error) {
      console.error('Ошибка привязки менеджера:', error)
      Swal.fire('Ошибка', 'Не удалось привязать менеджера мерчантов', 'error')
    } finally {
      setManagerLoading(false)
    }
  }

  const handleDetachManager = async (managerId: number) => {
    if (!cashbox) return
    setManagerLoading(true)
    try {
      const response = await detachCashBoxManager(cashbox.id, managerId)
      if (response.data?.success && response.data.cashbox) {
        setCashbox(response.data.cashbox)
      }
    } catch (error) {
      console.error('Ошибка отвязки менеджера:', error)
      Swal.fire('Ошибка', 'Не удалось отвязать менеджера мерчантов', 'error')
    } finally {
      setManagerLoading(false)
    }
  }

  const copyTokenToClipboard = async (token?: string | null, anchorAttr = 'data-copy-token') => {
    if (!token) return

    try {
      await navigator.clipboard.writeText(token)
      
      // Создаем временный tooltip
      const tooltip = document.createElement('div')
      tooltip.innerHTML = 'Токен скопирован!'
      tooltip.style.cssText = `
        position: absolute;
        background: #28a745;
        color: white;
        padding: 8px 12px;
        border-radius: 4px;
        font-size: 12px;
        z-index: 1050;
        top: -40px;
        left: 50%;
        transform: translateX(-50%);
        white-space: nowrap;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      `
      
             const iconElement = document.querySelector(`[${anchorAttr}]`) as HTMLElement
       if (iconElement) {
         iconElement.style.position = 'relative'
         iconElement.appendChild(tooltip)
        
        setTimeout(() => {
          if (tooltip.parentNode) {
            tooltip.parentNode.removeChild(tooltip)
          }
        }, 2000)
      }
    } catch (error) {
      console.error('Ошибка копирования:', error)
      Swal.fire('Ошибка', 'Не удалось скопировать токен', 'error')
    }
  }

  const handleToggleApiAccess = async (enabled: boolean) => {
    if (!cashbox) return

    if (!enabled) {
      const confirm = await Swal.fire({
        title: 'Выключить программный доступ?',
        text: `Интеграция кассы "${cashbox.title}" перестанет работать сразу. Ключ сохранится — при повторном включении он останется прежним.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Выключить',
        cancelButtonText: 'Отмена',
      })
      if (!confirm.isConfirmed) return
    }

    setApiAccessLoading(true)
    try {
      const response = await setCashBoxApiAccess(cashbox.id, enabled)
      if (response.data?.success) {
        setCashbox({
          ...cashbox,
          api_access_enabled: response.data.data.enabled,
          api_token: response.data.data.token,
        })
      }
    } catch (error) {
      console.error('Ошибка изменения программного доступа:', error)
      Swal.fire('Ошибка', 'Не удалось изменить программный доступ', 'error')
    } finally {
      setApiAccessLoading(false)
    }
  }

  const handleRotateApiToken = async () => {
    if (!cashbox) return

    const confirm = await Swal.fire({
      title: 'Перевыпустить ключ?',
      text: 'Прежний ключ перестанет работать немедленно — интеграцию кассы нужно будет перевести на новый.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Перевыпустить',
      cancelButtonText: 'Отмена',
    })
    if (!confirm.isConfirmed) return

    setApiAccessLoading(true)
    try {
      const response = await rotateCashBoxApiToken(cashbox.id)
      if (response.data?.success) {
        setCashbox({
          ...cashbox,
          api_access_enabled: response.data.data.enabled,
          api_token: response.data.data.token,
        })
      }
    } catch (error) {
      console.error('Ошибка перевыпуска ключа:', error)
      Swal.fire('Ошибка', 'Не удалось перевыпустить ключ', 'error')
    } finally {
      setApiAccessLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: ru })
  }

  const getStatusBadge = (enabled: boolean) => (
    <span className={`badge ${enabled ? 'badge-success' : 'badge-danger'}`}>
      {enabled ? 'Активна' : 'Неактивна'}
    </span>
  )

  const getRolesBadges = (roles: string[]) => {
    const roleNames: { [key: string]: string } = {
      'ROLE_CASHIER': 'Кассир',
      'ROLE_SENIOR_CASHIER': 'Старший кассир',
      'ROLE_USER': 'Пользователь',
      'ROLE_EMPLOYEE': 'Сотрудник'
    }

    if (!roles || !Array.isArray(roles)) {
      return null
    }

    return roles.map((role, index) => (
      <span key={index} className="badge badge-light-primary me-1">
        {roleNames[role] || role}
      </span>
    ))
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-10">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Загрузка...</span>
        </div>
      </div>
    )
  }

  if (!cashbox) {
    return (
      <div className="d-flex justify-content-center py-10">
        <div className="text-muted fs-4">Касса не найдена</div>
      </div>
    )
  }

  return (
    <div>
      {/* Хлебные крошки */}
      <div className="d-flex align-items-center mb-7">
        <button
          className="btn btn-sm btn-light me-3"
          onClick={() => navigate('/settings/cashboxes')}
        >
          <KTIcon iconName="arrow-left" className="fs-3" />
          Назад к списку касс
        </button>
        <h1 className="page-heading fw-bold fs-2 text-dark mb-0">
          Касса: {cashbox.title}
        </h1>
      </div>

      {/* Блок 1: Управление статусом */}
      <div className="card mb-7">
        <div className="card-header border-0 pt-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold fs-3 mb-1">Информация о кассе</span>
            <span className="text-muted mt-1 fw-semibold fs-7">
              Основные данные и управление статусом
            </span>
          </h3>
        </div>
        <div className="card-body py-3">
          <div className="row mb-7">
            <div className="col-lg-6">
              <div className="d-flex flex-column">
                <div className="fw-bold text-gray-800 mb-3">ID кассы:</div>
                <div className="text-gray-600">#{cashbox.id}</div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="d-flex flex-column">
                <div className="fw-bold text-gray-800 mb-3">Название:</div>
                <div className="text-gray-600">{cashbox.title}</div>
              </div>
            </div>
          </div>

          <div className="row mb-7">
            <div className="col-lg-6">
              <div className="d-flex flex-column">
                <div className="fw-bold text-gray-800 mb-3">Дата создания:</div>
                <div className="text-gray-600">{formatDate(cashbox.created_at)}</div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="d-flex flex-column">
                <div className="fw-bold text-gray-800 mb-3">Callback URL:</div>
                <div className="text-gray-600">
                  {cashbox.callback_url || 'Не указан'}
                </div>
              </div>
            </div>
          </div>

          <div className="row mb-7">
            <div className="col-lg-12">
              <div className="d-flex flex-column">
                <div className="fw-bold text-gray-800 mb-3">Токен для приёма СМС:</div>
                <div className="d-flex align-items-center">
                  <div className="text-gray-600 me-3">
                    {cashbox.auth_token || 'Не найден'}
                  </div>
                  {cashbox.auth_token && (
                    <span
                      data-copy-token
                      onClick={() => copyTokenToClipboard(cashbox.auth_token)}
                      style={{ cursor: 'pointer' }}
                      title="Скопировать токен в буфер обмена"
                    >
                      <KTIcon iconName="save-2" className="fs-1 text-muted" />
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* CPAYMENT-1693 (временное решение): программный доступ кассы */}
          {canManage && (
            <div className="row mb-7">
              <div className="col-lg-12">
                <div className="d-flex flex-column">
                  <div className="fw-bold text-gray-800 mb-3">Программный доступ:</div>
                  <div className="form-check form-switch form-check-custom form-check-solid mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="cashbox-api-access"
                      checked={cashbox.api_access_enabled === true}
                      disabled={apiAccessLoading}
                      onChange={(e) => handleToggleApiAccess(e.target.checked)}
                    />
                    <label className="form-check-label text-gray-700" htmlFor="cashbox-api-access">
                      {cashbox.api_access_enabled ? 'Разрешён' : 'Запрещён'}
                    </label>
                  </div>
                  <div className="text-muted fs-7 mb-3">
                    Касса может включать и выключать свои реквизиты и управлять клиентами Fast Track
                    без входа в интерфейс. Ключ передаётся команде кассы и не имеет срока действия.
                  </div>
                  {cashbox.api_access_enabled && (
                    <div className="d-flex align-items-center">
                      <div className="text-gray-600 me-3">{cashbox.api_token || 'Не найден'}</div>
                      {cashbox.api_token && (
                        <span
                          data-copy-api-token
                          onClick={() => copyTokenToClipboard(cashbox.api_token, 'data-copy-api-token')}
                          style={{ cursor: 'pointer' }}
                          title="Скопировать ключ в буфер обмена"
                          className="me-3"
                        >
                          <KTIcon iconName="save-2" className="fs-1 text-muted" />
                        </span>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-light-warning"
                        disabled={apiAccessLoading}
                        onClick={handleRotateApiToken}
                        title="Выдать новый ключ, старый перестанет работать"
                      >
                        <KTIcon iconName="arrows-circle" className="fs-4 me-1" />
                        Перевыпустить
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="row">
            <div className="col-lg-6">
              <div className="d-flex flex-column">
                <div className="fw-bold text-gray-800 mb-3">Статус:</div>
                <div className="d-flex align-items-center">
                  {getStatusBadge(cashbox.enabled)}
                  {canManage && (
                    <div className="form-check form-switch ms-5">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={cashbox.enabled}
                        onChange={(e) => handleStatusChange(e.target.checked)}
                        disabled={statusLoading}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Блокировка кассы (CPAYMENT-1546) и учёт её баланса (CPAYMENT-1572) — разные
              инструменты: блокировка стопит только выплаты и ставится руками, учёт баланса
              решает, возьмёт ли касса тикет на эту сумму. */}
          <div className="row mt-8">
            <div className="col-lg-6">
              <div className="d-flex flex-column">
                <div className="fw-bold text-gray-800 mb-3">Блокировка:</div>
                <div className="d-flex align-items-center">
                  <span className={`badge ${cashbox.blocked ? 'badge-danger' : 'badge-light-success'}`}>
                    {cashbox.blocked ? 'Заблокирована' : 'Нет'}
                  </span>
                  {canManageBlock && (
                    <button
                      className={`btn btn-sm ms-5 ${cashbox.blocked ? 'btn-light-success' : 'btn-light-danger'}`}
                      onClick={handleBlockToggle}
                    >
                      {cashbox.blocked ? 'Разблокировать' : 'Заблокировать'}
                    </button>
                  )}
                </div>
                {cashbox.blocked && cashbox.block_reason && (
                  <div className="text-muted fw-semibold mt-2">Причина: {cashbox.block_reason}</div>
                )}
                {cashbox.blocked && cashbox.blocked_at && (
                  <div className="text-muted fw-semibold">Заблокирована: {formatDate(cashbox.blocked_at)}</div>
                )}
                {cashbox.blocked && cashbox.blocked_by && (
                  <div className="text-muted fw-semibold">Кем: {cashbox.blocked_by}</div>
                )}
              </div>
            </div>

            {canManageBlock && (
              <div className="col-lg-6">
                <div className="d-flex flex-column">
                  <div className="fw-bold text-gray-800 mb-3">Учёт баланса:</div>
                  <CashBoxBalancePolicy cashBoxId={cashbox.id} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Блок: Баланс кошелька Ledger (manager/admin) */}
      {canManage && <LedgerWalletBalanceCard ownerType='cash-box' id={cashbox.id} />}

      {/* Блок 2: Сотрудники */}
      <div className="card">
        <div className="card-header border-0 pt-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold fs-3 mb-1">Сотрудники</span>
            <span className="text-muted mt-1 fw-semibold fs-7">
              Всего сотрудников: {cashbox.cashiers?.length || 0}
            </span>
          </h3>
          <div className="card-toolbar">
            {canManage && (
              <NewEmployee
                callback={handleEmployeeAdded}
                presetCashBoxId={cashbox.id}
                presetCashBoxTitle={cashbox.title}
                allowedRoles={ROLES_FOR_CASHBOX}
                buttonText="Добавить сотрудника"
                modalTitle={`Добавить сотрудника в кассу "${cashbox.title}"`}
                buttonClassName="btn btn-sm btn-primary"
                showIcon={true}
              />
            )}
          </div>
        </div>
        <div className="card-body py-3">
          {cashbox.cashiers && cashbox.cashiers.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                <thead>
                  <tr className="fw-bold text-muted">
                    <th className="min-w-150px">UUID</th>
                    <th className="min-w-200px">Роли</th>
                    <th className="min-w-100px">Статус</th>
                    <th className="min-w-150px">Дата создания</th>
                  </tr>
                </thead>
                <tbody>
                  {cashbox.cashiers.map((cashier: ICashier) => (
                    <tr key={cashier.id}>
                      <td>
                        <div className="text-dark fw-bold fs-6">
                          {cashier.uuid}
                        </div>
                      </td>
                      <td>
                        {getRolesBadges(cashier.roles)}
                      </td>
                      <td>
                        {getStatusBadge(cashier.enabled)}
                      </td>
                      <td>
                        <div className="text-muted fw-semibold">
                          {formatDate(cashier.createdAt)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="d-flex justify-content-center py-10">
              <div className="text-muted fs-4">
                В этой кассе пока нет сотрудников
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Блок 3: Менеджеры мерчантов (AC4, только ADMIN/MANAGER) */}
      {canManage && (
        <div className="card mt-7">
          <div className="card-header border-0 pt-5">
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label fw-bold fs-3 mb-1">Менеджеры мерчантов</span>
              <span className="text-muted mt-1 fw-semibold fs-7">
                Доступ к кассе для роли «Менеджер мерчантов»: {cashbox.merchant_managers?.length || 0}
              </span>
            </h3>
            <div className="card-toolbar">
              <div className="d-flex align-items-center">
                <select
                  className="form-select form-select-sm w-250px me-2"
                  value={selectedManagerId}
                  onChange={(e) => setSelectedManagerId(e.target.value)}
                  disabled={managerLoading}
                >
                  <option value="">Выберите менеджера…</option>
                  {availableManagers
                    .filter(
                      (m) => !cashbox.merchant_managers?.some((bound) => bound.id === m.id)
                    )
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.uuid}
                      </option>
                    ))}
                </select>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleAttachManager}
                  disabled={managerLoading || !selectedManagerId}
                >
                  <KTIcon iconName="plus" className="fs-3" />
                  Привязать
                </button>
              </div>
            </div>
          </div>
          <div className="card-body py-3">
            {cashbox.merchant_managers && cashbox.merchant_managers.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                  <thead>
                    <tr className="fw-bold text-muted">
                      <th className="min-w-200px">UUID</th>
                      <th className="min-w-100px text-end">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cashbox.merchant_managers.map((manager) => (
                      <tr key={manager.id}>
                        <td>
                          <div className="text-dark fw-bold fs-6">{manager.uuid}</div>
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-light-danger"
                            onClick={() => handleDetachManager(manager.id)}
                            disabled={managerLoading}
                          >
                            <KTIcon iconName="trash" className="fs-4" />
                            Отвязать
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="d-flex justify-content-center py-10">
                <div className="text-muted fs-4">
                  К этой кассе не привязан ни один менеджер мерчантов
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default CashBoxDetails 